import DOMPurify from "dompurify";
import { setSpeeds } from "utils";
import { CardModel } from "atoms/models";
import ViewDetailsModal from "./ViewDetailsModal";
import ChannelGuide from "./ChannelGuide";
import { setSavings } from "utils";

interface ContentObjectModel {
  content: string;
  contentType: string;
  fieldName: string;
  fileURL: string;
}

interface ProductBenefitsProps {
  card: CardModel;
  showSpeedsOnly?: boolean;
}

interface FieldName {
  fieldName: string;
}

interface Copy {
  fieldName: string;
  contentType: string;
  content: string;
  fileURL: string;
}

const ProductBenefits: React.FC<ProductBenefitsProps> = ({ card, showSpeedsOnly }) => {
  const { contentEcommerce } = card;

  const benefitOneTop: ContentObjectModel | undefined = contentEcommerce.find((obj: FieldName) => obj.fieldName === "benefit1Top");
  const benefitOneBottom: ContentObjectModel | undefined = contentEcommerce.find((obj: FieldName) => obj.fieldName === "benefit1Bottom");
  const benefitTwoTop: ContentObjectModel | undefined = contentEcommerce.find((obj: FieldName) => obj.fieldName === "benefit2Top");
  const benefitTwoBottom: ContentObjectModel | undefined = contentEcommerce.find((obj: FieldName) => obj.fieldName === "benefit2Bottom");
  const benefitThreeTop: ContentObjectModel | undefined = contentEcommerce.find((obj: FieldName) => obj.fieldName === "benefit3Top");
  const benefitThreeBottom: ContentObjectModel | undefined = contentEcommerce.find((obj: FieldName) => obj.fieldName === "benefit3Bottom");
  const benefitFourTop: ContentObjectModel | undefined = contentEcommerce.find((obj: FieldName) => obj.fieldName === "benefit4Top");
  const benefitFourBottom: ContentObjectModel | undefined = contentEcommerce.find((obj: FieldName) => obj.fieldName === "benefit4Bottom");
  const copy: ContentObjectModel | undefined = contentEcommerce.find((obj: FieldName) => obj.fieldName === "copy");
  const savings: ContentObjectModel | undefined = contentEcommerce.find((obj: FieldName) => obj.fieldName === "savings");

  const setContentType = (contentObject: ContentObjectModel) => {
    if (contentObject?.contentType === "Image") {
      return <img src={contentObject.fileURL} alt="Comporium" className="card-content-image" />;
    } else if (contentObject?.contentType === "Rich Text" || contentObject?.contentType === "Text") {
      const cleanedContent = contentObject.content.replace(/\?/g, ""); // remove question marks

      const sanitizedHtml = DOMPurify.sanitize(cleanedContent, {
        ALLOWED_TAGS: ["span", "strong", "p", "ul", "li"], // Only allow <span> tags
        FORBID_ATTR: ["style"], // Remove style attributes
      });

      const formattedTextWithSpeedUnits: string = setSpeeds(sanitizedHtml, card);
      return <span dangerouslySetInnerHTML={{ __html: formattedTextWithSpeedUnits }} />;
    } else {
      return null;
    }
  };

  // Replaces any empty "<p> </p>" in the content field to
  // ensure view details button does not show in case of empty product copy
  const sanitizedCopy: Copy | undefined = copy
    ? {
        ...copy,
        content: copy.content.replace(/<p>(?:\s|&nbsp;)*<\/p>/gi, ""),
      }
    : copy;

  return (
    <>
      <table className="table table-borderless benefits">
        <tbody>
          <tr>
            <td>
              <div className="benefit-top">{benefitOneTop ? setContentType(benefitOneTop) : ""}</div>
            </td>
            <td>
              <div className="benefit-bottom">{benefitOneBottom ? setContentType(benefitOneBottom) : ""}</div>
            </td>
          </tr>
          {showSpeedsOnly ? null : (
            <>
              <tr>
                <td>
                  <div className="benefit-top">{benefitTwoTop ? setContentType(benefitTwoTop) : ""}</div>
                </td>
                <td>
                  <div className="benefit-bottom">{benefitTwoBottom ? setContentType(benefitTwoBottom) : ""}</div>
                </td>
              </tr>
              <tr>
                <td>
                  <div className="benefit-top">{benefitThreeTop ? setContentType(benefitThreeTop) : ""}</div>
                </td>
                <td>
                  <div className="benefit-bottom">{benefitThreeBottom ? setContentType(benefitThreeBottom) : null}</div>
                </td>
              </tr>
              <tr>
                <td>
                  <div className="benefit-top">{benefitFourTop ? setContentType(benefitFourTop) : ""}</div>
                </td>
                <td>
                  <div className="benefit-bottom">{benefitFourBottom ? setContentType(benefitFourBottom) : ""}</div>
                </td>
              </tr>
            </>
          )}
        </tbody>
      </table>
      {card?.streamTv ? <ChannelGuide /> : null}
      <div className="card-savings text-center mt-3">
        <strong className="fw-light">{setSavings(savings?.content, card)}</strong>
      </div>
      {sanitizedCopy?.content && !showSpeedsOnly ? <ViewDetailsModal copy={sanitizedCopy.content} card={card} /> : null}
    </>
  );
};

export default ProductBenefits;
